/**
 * Cookie utility functions that respect user consent
 */

/**
 * Check if the user has consented to cookies
 * @returns {boolean} True if user has consented, false otherwise
 */
export const hasUserConsent = () => {
  const consentCookie = getCookieValue('protegoCookieConsent');
  // The user has explicitly consented
  return consentCookie === 'true';
};

/**
 * Gets a cookie value by name
 * @param {string} name - Cookie name
 * @returns {string|null} Cookie value or null if not found
 */
export const getCookieValue = (name) => {
  const cookies = document.cookie.split(';');
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].trim();
    if (cookie.startsWith(name + '=')) {
      return cookie.substring(name.length + 1);
    }
  }
  return null;
};

/**
 * Sets a cookie only if the user has consented
 * If the user hasn't consented, this will not set the cookie
 * 
 * @param {string} name - Cookie name
 * @param {string} value - Cookie value
 * @param {number} daysToExpire - Cookie expiration in days
 * @returns {boolean} True if cookie was set, false if not (due to lack of consent)
 */
export const setCookieWithConsent = (name, value, daysToExpire = 30) => {
  // Don't set cookies if the user hasn't consented (except for the consent cookie itself)
  if (!hasUserConsent() && name !== 'protegoCookieConsent') {
    return false;
  }
  
  const expirationDate = new Date();
  expirationDate.setDate(expirationDate.getDate() + daysToExpire);
  
  document.cookie = `${name}=${value};expires=${expirationDate.toUTCString()};path=/`;
  return true;
};

/**
 * Removes a cookie
 * @param {string} name - Cookie name
 */
export const removeCookie = (name) => {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
};

/**
 * Removes all non-essential cookies
 * This preserves the consent cookie itself but removes all others
 */
export const removeAllCookies = () => {
  const cookies = document.cookie.split(';');
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i];
    const eqPos = cookie.indexOf('=');
    const name = eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();
    // Skip the consent cookie itself
    if (name !== 'protegoCookieConsent') {
      removeCookie(name);
    }
  }
}; 